import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { TripsPage } from './pages/TripsPage';
import { ReportsPage } from './pages/ReportsPage';
import { DriversPage } from './pages/DriversPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { UsersPage } from './pages/UsersPage';
import { SettingsPage } from './pages/SettingsPage';
import { CompaniesPage } from './pages/CompaniesPage';
import { DeviceInventoryPage } from './pages/DeviceInventoryPage';
import { DeviceLogsPage } from './pages/DeviceLogsPage';
import { MaintenancePage } from './pages/MaintenancePage';
import { VehicleDetailPage } from './pages/VehicleDetailPage';
import { UserDetailPage } from './pages/UserDetailPage';
import { DriverDetailPage } from './pages/DriverDetailPage';
import { MyVehiclePage } from './pages/MyVehiclePage';
import { SmartcarWebhookEventsPage } from './pages/SmartcarWebhookEventsPage';
import { QualificationRulesPage } from './pages/QualificationRulesPage';
import { FleetHealthPage } from './pages/FleetHealthPage';

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/trips"
        element={
          <ProtectedRoute>
            <TripsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drivers"
        element={
          <ProtectedRoute>
            <DriversPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles"
        element={
          <ProtectedRoute>
            <VehiclesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/companies"
        element={
          <ProtectedRoute>
            <CompaniesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/device-inventory"
        element={
          <ProtectedRoute>
            <DeviceInventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/device-logs"
        element={
          <ProtectedRoute>
            <DeviceLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance"
        element={
          <ProtectedRoute>
            <MaintenancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles/:id"
        element={
          <ProtectedRoute>
            <VehicleDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:id"
        element={
          <ProtectedRoute>
            <UserDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drivers/:id"
        element={
          <ProtectedRoute>
            <DriverDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-vehicle"
        element={
          <ProtectedRoute>
            <MyVehiclePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/webhook-events"
        element={
          <ProtectedRoute>
            <SmartcarWebhookEventsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qualification-rules"
        element={
          <ProtectedRoute>
            <QualificationRulesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/fleet-health"
        element={
          <ProtectedRoute>
            <FleetHealthPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
